import { Button, SelectOptionType } from '@octano/global-ui';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Col, Form, Row } from 'reactstrap';

import { useTexts } from '../../hooks/textsContext';
import useUpdateAction from '../../hooks/useUpdateAction';
import { FormDataAction, StructureTypeCode } from '../../interfaces/type';
import { useCreateEvaluationFormContext } from '../EvaluationForm/context/CreateEvaluationFormContext';
import { GradesConfig } from '../EvaluationForm/types';
import HeaderStructureTypeFormSemiStaticFields from '../Header/HeaderStructureTypeFormSemiStaticFields';
import { useGradingStructureInfoLoader } from '../Loaders/GradingStructureInfoLoader';
import UpdateStructureTypeFormFields from './UpdateStructureTypeFormFields';
import UpdateStructureTypeFormFreeFields from './UpdateStructureTypeFormFreeFields';
import UpdateStructureTypeFormSemiStaticFields from './UpdateStructureTypeFormSemiStaticFields';
import useQuery from '../../../../../hooks/useQuery';
import { DeliveryModes, StructureTypes } from '../../types';
import { useGradingStructureFormLoader } from '../Loaders/GradingStructureFormLoader';
import useCreateAction from '../../hooks/useCreateAction';

export function UpdateStructureTypeForm(): JSX.Element {
  const texts = useTexts();

  const { data: initialValues } = useGradingStructureInfoLoader();
  const {
    gradeEntry,
    handleValidate,
    setGradeEntry,
  } = useCreateEvaluationFormContext();
  const { update } = useUpdateAction();
  const { create } = useCreateAction();
  const { data } = useGradingStructureFormLoader();

  const methods = useForm<{
    id: number | null;
    structureTypeId: SelectOptionType | null;
    finalGrade: GradesConfig['finalGrade'];
    testGrades: GradesConfig['testGrades'];
  }>({
    mode: 'onChange',
    defaultValues: {
      structureTypeId: {
        value: initialValues?.gradesStructure?.structureType?.id,
        label: initialValues?.gradesStructure?.structureType?.name,
      },
    },
  });
  const { handleSubmit, watch, formState, setValue } = methods;
  const { isSubmitting } = formState;
  const structureType = watch('structureTypeId');
  const query = useQuery();
  const deliveryMode = query.get('deliveryMode');

  const onlineDeliveryMode = useMemo(() => {
    return deliveryMode === DeliveryModes.OL;
  }, [deliveryMode]);

  useEffect(() => {
    if (
      onlineDeliveryMode &&
      structureType?.value === StructureTypeCode.FIXED
    ) {
      setGradeEntry((prev) => {
        return {
          ...prev,
          finalGrade: {
            ...prev.finalGrade,
            onlinePercentage: 100,
          },
        };
      });
    }
  }, [onlineDeliveryMode, structureType, setGradeEntry]);

  const onSubmit = useCallback(
    async (values: FormDataAction) => {
      if (initialValues?.gradesStructure?.id) {
        if (values?.structureTypeId?.value === StructureTypeCode.FREE) {
          return update(initialValues.gradesStructure.id, {
            ...values,
            finalGrade: null,
            testGrades: null,
          });
        }

        if (
          values?.structureTypeId?.label === StructureTypes.FIXED &&
          onlineDeliveryMode
        ) {
          return update(initialValues.gradesStructure.id, {
            ...values,
            finalGrade: gradeEntry.finalGrade,
            testGrades: gradeEntry.testGrades,
          });
        }

        return handleValidate(() =>
          update(initialValues.gradesStructure.id, {
            ...values,
            finalGrade: gradeEntry.finalGrade,
            testGrades: gradeEntry.testGrades,
          }),
        )();
      } else {
        if (
          values?.structureTypeId?.value === StructureTypeCode.FREE ||
          (values?.structureTypeId?.label === StructureTypes.FIXED &&
            onlineDeliveryMode)
        ) {
          return create({
            ...values,
            finalGrade: gradeEntry.finalGrade,
            testGrades: gradeEntry.testGrades,
            deliveryMode: deliveryMode as DeliveryModes,
            academicLevelId: initialValues?.academicLevel?.id,
          });
        }
        return handleValidate(() =>
          create({
            ...values,
            finalGrade: gradeEntry.finalGrade,
            testGrades: gradeEntry.testGrades,
            deliveryMode: deliveryMode as DeliveryModes,
            academicLevelId: initialValues?.academicLevel?.id,
          }),
        )();
      }
    },
    [
      initialValues?.gradesStructure?.id,
      gradeEntry,
      handleValidate,
      update,
      create,
      deliveryMode,
      initialValues?.academicLevel?.id,
      onlineDeliveryMode,
    ],
  );

  const fixedStructureType = useMemo(() => {
    return data?.structureType?.filter(
      (item) => item.name === StructureTypes.FIXED,
    );
  }, [data?.structureType]);

  useEffect(() => {
    if (onlineDeliveryMode && fixedStructureType?.length) {
      setValue(
        'structureTypeId',
        {
          value: fixedStructureType[0].id,
          label: fixedStructureType[0].name,
        },
        { shouldValidate: true, shouldDirty: true },
      );
    }
  }, [onlineDeliveryMode, fixedStructureType, setValue]);

  return (
    <FormProvider {...methods}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <UpdateStructureTypeFormFields
          deliveryMode={deliveryMode as DeliveryModes}
        />

        {structureType?.value === StructureTypeCode.FIXED && (
          <article>
            <HeaderStructureTypeFormSemiStaticFields
              msgInfo={
                !onlineDeliveryMode ? texts.form.msg.msgInfoFixed : undefined
              }
            />
            <UpdateStructureTypeFormSemiStaticFields />
          </article>
        )}

        {structureType?.value === StructureTypeCode.SEMIFIXED && (
          <article>
            <HeaderStructureTypeFormSemiStaticFields
              msgInfo={texts.form.msg.msgInfoSemiFixed}
            />
            <UpdateStructureTypeFormSemiStaticFields />
          </article>
        )}

        {structureType?.value === StructureTypeCode.FREE && (
          <article>
            <UpdateStructureTypeFormFreeFields />
          </article>
        )}

        <Row className="justify-content-end p-0 m-0">
          <Col xs={12} sm={4} className="p-0 m-0">
            <Button
              size="md"
              type="submit"
              text={texts.actions.save}
              fullwidth
              loading={isSubmitting}
            />
          </Col>
        </Row>
      </Form>
    </FormProvider>
  );
}
